import axios from "axios";
import Util from '../utils/Util';
import authUtil from "../utils/AuthUtils";
import notify from "../utils/notifier";
import { getServiceApiKey } from '../utils/apiKey';

const BASE_API_URL = `${window?.appConfig?.API_ENDPOINT}`;

const DEFAULT_STAGING_REASONS = [];


// Helper function to create config
const getConfig = async () => {
  const token = await authUtil.getDocumentServiceToken();
  return {
    headers: {
      "x-api-key": getServiceApiKey(),
      "x-document-service-integration-token": token,
    },
  };
};


async function getAllTemplates(queryParams = {}) {
  const config = await getConfig();  // Await the token asynchronously
  // Build query string from queryParams
  let url = BASE_API_URL + `/document-suite/document-types`;
  const keys = Object.keys(queryParams || {}).filter(k => queryParams[k] !== undefined && queryParams[k] !== null && String(queryParams[k]).trim() !== '');
  if (keys.length) {
    url += '?';
    const parts = keys.map(k => {
      let val = String(queryParams[k]);
      if (k === '_order') {
        const v = val.toLowerCase();
        val = (v === 'asc' || v === 'desc') ? v : 'asc';
      }
      return `${encodeURIComponent(k)}=${encodeURIComponent(val)}`;
    });
    url += parts.join('&');
  }
  return axios.get(url, {
    headers: {
      ...config.headers
    },
  });
}


async function createTemplate(requestObj) {
  let clientName = authUtil.getClientInfo();
  const config = await getConfig();  // Await the token asynchronously
  return axios.post(BASE_API_URL + `/document-suite/document-type`, requestObj,
    {
      headers: {
        'X-DPW-ApplicationId': clientName,
        ...config.headers
      },
    });
}

async function createSearchKeys(requestObj) {
  let clientName = authUtil.getClientInfo();
  const config = await getConfig();  // Await the token asynchronously
  return axios.post(BASE_API_URL + `/file/search-keys`, requestObj,
    {
      headers: {
        'X-DPW-ApplicationId': clientName,
        ...config.headers
      },
    });
}

async function getSearchKeys() {
  const config = await getConfig();  // Await the token asynchronously
  return axios.get(BASE_API_URL + `/document-suite/document/search-fields`,
    {
      headers: {
        ...config.headers
      },
    });
}

async function getDocumentFieldValues(fieldName, queryParams = {}) {
  const config = await getConfig();
  let url = BASE_API_URL + `/document-suite/document-field-values/${encodeURIComponent(fieldName)}`;
  const keys = Object.keys(queryParams || {}).filter(k => queryParams[k] !== undefined && queryParams[k] !== null && String(queryParams[k]).trim() !== '');
  if (keys.length) {
    url += '?';
    const parts = keys.map(k => `${encodeURIComponent(k)}=${encodeURIComponent(String(queryParams[k]))}`);
    url += parts.join('&');
  }
  return axios.get(url, {
    headers: {
      ...config.headers
    }
  });
}

async function searchDocuments(requestObj, queryParams) {
  const stagingReasons = requestObj?.stagingReasons?.length
    ? requestObj.stagingReasons
    : DEFAULT_STAGING_REASONS;
  const requestPayload = stagingReasons.length
    ? { ...requestObj, stagingReasons }
    : requestObj;

  const config = await getConfig();  // Await the token asynchronously
  const fieldMask = '*';
  let url = BASE_API_URL + `/document-suite/document/search`;
  let i = 0;
  for (let param of Object.keys(queryParams)) {
    url = url + `${(i == 0 ? '?' : '&')}` + param + `=${queryParams[param]}`;
    i = 1;
  }
  return axios.post(url, requestPayload,
    {
      headers: {
        'x-ds-fieldmask': fieldMask,
        ...config.headers
      },
    });
}

async function getRecycleDocuments(queryParams) {
  const config = await getConfig();  // Await the token asynchronously
  let url = BASE_API_URL + `/document-suite/recycle-bin`;
  let i = 0;
  for (let param of Object.keys(queryParams)) {
    url = url + `${(i == 0 ? '?' : '&')}` + param + `=${queryParams[param]}`;
    i = 1;
  }
  return axios.get(url,
    {
      headers: {
        ...config.headers
      },
    });
}
async function retryFiles(fileIdentifiers) {
    const config = await getConfig();
    const url = BASE_API_URL + `/file-storage/workflow-processing/files/bulk-retrigger`;
    return axios.post(
        url,
        { fileIdentifiers },
        {
            headers: {
                ...config.headers
            }
        }
    );
}
async function viewDocument(id) {
  const config = await getConfig();
  return axios.get(BASE_API_URL + `/document-suite/document/download?_documentId=` + id,
    {
      headers: {
        ...config.headers
      },
      maxBodyLength: Infinity,
      responseType: 'blob',
    });
}

async function moveToFolder(id) {
  const config = await getConfig();
  return axios.put(BASE_API_URL + `/document-suite/document/folder?_documentId=` + id, {},
    {
      headers: {
        ...config.headers
      }
    });
}

async function moveToFolderByName(id, name) {
  const config = await getConfig();
  return axios.put(BASE_API_URL + `/document-suite/document/folder?_documentId=` + id + '&folderName=' + name, {},
    {
      headers: {
        ...config.headers
      }
    });
}

async function moveToFolderByFileId(fileIdentifier) {
  const config = await getConfig();
  return axios.put(BASE_API_URL + `/document-suite/document/folder?_fileIdentifier=` + fileIdentifier, {},
    {
      headers: {
        ...config.headers
      }
    });
}

const fetchDocumentTypeDetails = async (documentTypeId) => {
  try {
    const user = authUtil.getClientInfo();
    const config = await getConfig();
    const apiUrl = `${BASE_API_URL}/file-storage/document-type/${documentTypeId}?user=${user}`;
    console.log('Service - API URL:', apiUrl);

    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        ...config.headers
      }
    });

    console.log('Service - Response status:', response.status);

    if (response.ok) {
      const data = await response.json();
      console.log('Service - Received data:', data);
      return data;
    } else {
      const errorText = await response.text();
      console.error('Service - API Error Response:', errorText);
      throw new Error(`Failed to fetch document type details: ${response.status} ${response.statusText}`);
    }
  } catch (error) {
    console.error('Service - Error fetching document type details:', error);
    throw error; // Re-throw to let the component handle it
  }
};

// Also add the bulk update method to the service
const bulkUpdateMetadata = async (requestObj) => {
  try {
    const config = await getConfig();
    console.log('Service - Sending bulk update request:', requestObj);

    const response = await fetch(BASE_API_URL + '/file-storage/bulk-update-metadata', {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
        "x-api-key": getServiceApiKey(),
      },
      body: JSON.stringify(requestObj)
    });

    if (response.ok) {
      const data = await response.json();
      console.log('Service - Update response:', data);
      return data;
    } else {
      const errorText = await response.text();
      console.error('Service - Update Error Response:', errorText);
      throw new Error(`Failed to update document type: ${response.status} ${response.statusText}`);
    }
  } catch (error) {
    console.error('Service - Error updating document type:', error);
    throw error;
  }
};

async function fetchDocumentDetails(id) {
  const config = await getConfig();
  return axios.get(BASE_API_URL + `/document-suite/document?_documentId=` + id,
    {
      headers: {
        ...config.headers
      }
    });
}

async function updateMetadata(requestObj, id) {
  const config = await getConfig();
  return axios.put(BASE_API_URL + `/document-suite/document?_documentId=` + id, requestObj,
    {
      headers: {
        ...config.headers
      }
    });

}

async function softDelete(requestObj) {
  const config = await getConfig();
  return axios.post(BASE_API_URL + `/document-suite/documents/delete`, requestObj,
    {
      headers: {
        ...config.headers
      }
    });

}

async function permanentDelete(requestObj) {
  const config = await getConfig();
  return axios.delete(BASE_API_URL + `/document-suite/documents/permanent-delete`,
    {
      data: {
        ...requestObj
      },
      headers: {
        ...config.headers
      }
    });

}

async function restore(requestObj) {
  const config = await getConfig();
  return axios.post(BASE_API_URL + `/document-suite/documents/restore`, requestObj,
    {
      headers: {
        ...config.headers
      }
    });

}

async function emptyRecycleBin() {
  const config = await getConfig();
  return axios.delete(BASE_API_URL + `/document-suite/recycle-bin`,
    {
      headers: {
        ...config.headers
      }
    });
}

async function getClientFields(queryParams = {}) {
  const config = await getConfig();
  let url = BASE_API_URL + `/document-suite/client-field`;
  const keys = Object.keys(queryParams || {}).filter(k => queryParams[k] !== undefined && queryParams[k] !== null && String(queryParams[k]).trim() !== '');
  if (keys.length) {
    url += '?';
    const parts = keys.map(k => {
      let val = String(queryParams[k]);
      if (k === '_order') {
        const v = val.toLowerCase();
        val = (v === 'asc' || v === 'desc') ? v : 'asc';
      }
      return `${encodeURIComponent(k)}=${encodeURIComponent(val)}`;
    });
    url += parts.join('&');
  }
  return axios.get(url,
    {
      headers: {
        ...config.headers
      }
    });
}


async function createClientField(requestObj) {
  const config = await getConfig();
  return axios.post(BASE_API_URL + `/document-suite/client-field`, requestObj,
    {
      headers: {
        'Content-Type': 'application/json',
        ...config.headers
      },
    });
}
async function updateClientField(id, requestObj) {
  const config = await getConfig();
  return axios.put(BASE_API_URL + `/document-suite/client-field/${encodeURIComponent(id)}`, requestObj,
    {
      headers: {
        'Content-Type': 'application/json',
        ...config.headers
      },
    });
}
// Soft-delete (mark isActive = false) for client field
async function deleteClientField(id) {
  const config = await getConfig();
  const body = { isActive: false };
  return axios.post(BASE_API_URL + `/document-suite/client-field/${encodeURIComponent(id)}`, body,
    {
      headers: {
        'Content-Type': 'application/json',
        ...config.headers
      },
    });
}

async function deleteDocumentType(documentTypeId) {
  const config = await getConfig();
  return axios.delete(BASE_API_URL + `/document-suite/document-type/${encodeURIComponent(documentTypeId)}`,
    {
      headers: {
        ...config.headers
      },
    });
}

async function uploadFile(requestObj, file) {
  const formData = new FormData();
  formData.append('files', file);
  let organizationId = requestObj.organizationId ? requestObj.organizationId : '00000';
  let newFileName = requestObj.organizationId ? requestObj.newFileName : file.name;
  let path = requestObj.organizationId ? requestObj.path : '';

  formData.append('organizationId', organizationId);
  formData.append('newFileName', newFileName);
  formData.append('path', path);
  if (requestObj.documentCategory) {
    formData.append('documentCategory', requestObj.documentCategory);
  }
  let clientName = authUtil.getClientInfo();
  clientName = clientName ? clientName : 'default';
  const applicationId = requestObj.applicationId ? requestObj.applicationId : clientName;
  const config = await getConfig();


  try {
    const response = await axios.post(`${BASE_API_URL}/file-storage`, formData, {
      headers: {
        ...config.headers,
        'Content-Type': 'multipart/form-data',
        'X-Api-Version': 'v2',
        "X-DPW-ApplicationId": applicationId
      },
      withCredentials: true
    });

    return response.data;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }

}

async function getClients() {
  const config = await getConfig();
  return axios.get(BASE_API_URL + `/file-storage/clients`,
    {
      headers: {
        ...config.headers
      }
    });
}

async function getAntivirusSettings() {
  const config = await getConfig();
  return axios.get(BASE_API_URL + `/file-storage/antivirus/settings`,
    {
      headers: {
        ...config.headers
      }
    });
}


async function getSharedClientDetails(fileIdentifier) {
  const config = await getConfig();
  return axios.get(BASE_API_URL + `/file-storage/documents/${fileIdentifier}/shared-clients`,
    {
      headers: {
        ...config.headers
      }
    });
}

async function shareFiles(fileIdentifiers, addSharedClients = [], removeSharedClients = []) {
  let requestObj = { fileIdentifiers }
  
  // Handle both old format (array of strings) and new format (array of objects with client and accessType)
  if (addSharedClients.length) {
    // Check if it's the new format with accessType
    if (typeof addSharedClients[0] === 'object' && addSharedClients[0].client) {
      requestObj.addSharedClients = addSharedClients;
    } else {
      // Old format: convert strings to objects with default 'view' access
      requestObj.addSharedClients = addSharedClients.map(client => ({
        client: client,
        accessType: 'view'
      }));
    }
  }
  
  if (removeSharedClients.length) {
    requestObj.removeSharedClients = removeSharedClients;
  }
  
  if (addSharedClients.length == 0 && removeSharedClients.length == 0) {
    return;
  }
  
  const config = await getConfig();
  return axios.post(BASE_API_URL + `/file-storage/documents/share`, requestObj,
    {
      headers: {
        ...config.headers
      }
    });
}

// Document Classification - Categories
async function getCategories() {
  const config = await getConfig();
  return axios.get(BASE_API_URL + `/document-classification/categories`, {
    headers: {
      ...config.headers
    }
  });
}

async function createCategory(requestObj) {
  const config = await getConfig();
  return axios.post(BASE_API_URL + `/document-classification/categories`, requestObj, {
    headers: {
      'Content-Type': 'application/json',
      ...config.headers
    }
  });
}

async function updateCategory(categoryId, requestObj) {
  const config = await getConfig();
  return axios.put(BASE_API_URL + `/document-classification/categories/${encodeURIComponent(categoryId)}`, requestObj, {
    headers: {
      'Content-Type': 'application/json',
      ...config.headers
    }
  });
}

async function deleteCategory(categoryId) {
  const config = await getConfig();
  return axios.delete(BASE_API_URL + `/document-classification/categories/${encodeURIComponent(categoryId)}`, {
    headers: {
      ...config.headers
    }
  });
}

// Document Classification - Samples
async function getCategorySamples(categoryId) {
  const config = await getConfig();
  return axios.get(BASE_API_URL + `/document-classification/categories/${encodeURIComponent(categoryId)}/samples`, {
    headers: {
      ...config.headers
    }
  });
}

async function uploadCategorySample(categoryId, files) {
  const config = await getConfig();
  const formData = new FormData();
  
  if (Array.isArray(files)) {
    files.forEach(file => {
      formData.append('files', file);
    });
  } else {
    formData.append('files', files);
  }

  return axios.post(
    BASE_API_URL + `/document-classification/categories/${encodeURIComponent(categoryId)}/samples`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...config.headers
      }
    }
  );
}

async function deleteCategorySample(sampleId) {
  const config = await getConfig();
  return axios.delete(BASE_API_URL + `/document-classification/samples/${encodeURIComponent(sampleId)}`, {
    headers: {
      ...config.headers
    }
  });
}


const Api = {
  getAllTemplates,
  createTemplate,
  createSearchKeys,
  getSearchKeys,
  getDocumentFieldValues,
  searchDocuments,
  viewDocument,
  fetchDocumentTypeDetails,
  bulkUpdateMetadata,
  fetchDocumentDetails,
  getClientFields,
  createClientField,
  updateClientField,
  deleteClientField,
  deleteDocumentType,
  updateMetadata,
  uploadFile,
  getClients,
  getSharedClientDetails,
  shareFiles,
  softDelete,
  permanentDelete,
  restore,
  retryFiles,
  emptyRecycleBin,
  getRecycleDocuments,
  moveToFolder,
  moveToFolderByFileId,
  moveToFolderByName,
  getAntivirusSettings,
  // Document Classification
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  getCategorySamples,
  uploadCategorySample,
  deleteCategorySample
};

export default Api;
